import React, { useEffect, useRef } from 'react';
import { Check, X, User, Maximize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { LottieResult } from '../../services/lottieService';

interface LottiePreviewPanelProps {
  animation: LottieResult;
  onConfirm: (animation: LottieResult) => void;
  onCancel: () => void;
}


export const LottiePreviewPanel: React.FC<LottiePreviewPanelProps> = ({
  animation,
  onConfirm,
  onCancel,
}) => {
  const scriptLoadedRef = useRef(false);

  // Load lottie-player script if not already loaded
  useEffect(() => {
    if (scriptLoadedRef.current) return;

    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src*="lottie-player"]'
    );
    if (existingScript) {
      scriptLoadedRef.current = true;
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@lottiefiles/lottie-player@2.0.12/dist/lottie-player.js';
    script.async = true;
    document.head.appendChild(script);
    scriptLoadedRef.current = true;

    return () => {
      // Don't remove the script on unmount as other components may need it
    };
  }, []);

  // Handle confirm
  const handleConfirm = () => {
    onConfirm(animation);
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium truncate">Preview Animation</h3>
          <p className="text-xs text-muted-foreground truncate">
            {animation.name}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel} className="flex-shrink-0 ml-2">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Preview */}
      <div className="flex-1 flex items-center justify-center p-6 bg-muted/20">
        <div className="p-4 rounded-lg border-2 border-dashed border-border bg-white flex items-center justify-center">
          <lottie-player
            src={animation.jsonUrl}
            background="transparent"
            speed="1"
            style={{ width: '200px', height: '200px' }}
            loop
            autoplay
          />
        </div>
      </div>

      {/* Info */}
      <div className="px-4 py-3 border-t border-border space-y-2">
        {/* Author */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <User className="h-3 w-3" />
          <span className="truncate">{animation.author}</span>
        </div>

        {/* Dimensions */}
        {animation.dimensions && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Maximize2 className="h-3 w-3" />
            <span>{animation.dimensions.width} x {animation.dimensions.height}</span>
          </div>
        )}

        {/* Tags */}
        {animation.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {animation.tags.slice(0, 5).map((tag, index) => (
              <span
                key={index}
                className="px-1.5 py-0.5 text-[9px] bg-muted rounded text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border bg-muted/30">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleConfirm}>
          <Check className="h-3.5 w-3.5 mr-1" />
          Use Animation
        </Button>
      </div>
    </div>
  );
};

export default LottiePreviewPanel;
